import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AttemptFilesModal from 'components/@common/Modals/AttemptFilesModal';
import { get } from 'lodash';
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getStudent } from 'state/modules/entity/selectors/student-selectors';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { translate } from 'translations/translator';
import { Assignment, Attempt } from 'types';
import AttemptStatusChip from './AttemptStatusChip';
import AttemptStatusDropdown from './AttemptStatusDropdown';
import { AssignmentFilters } from './Filters';
import { SATableHeader } from './SATableHeader';
import { SATableLayout } from './SATableLayout';
import { formatUiDate } from 'util/date-util';
import { BaseModalTitle } from 'components/@common/Modals/BaseModalTitle';
import { TableColumnProps } from '@lwtears/lwt-common-frontend/lib/Table';

interface Props {
  assignments: Assignment[];
  filters: AssignmentFilters;
  onAddAssignmentClick: () => void;
}

interface AssignmentAttempt {
  assignment: Assignment;
  attempt: Attempt;
}

export enum COLUMNS_KEYS {
  ASSIGNMENT_NAME = 'title',
  ASSIGNED_DATE = 'dateAssigned',
  DUE_DATE = 'dateDue',
  STATUS = 'attemptStatus',
  FILES = 'attemptFiles',
  ACTION = ''
}

const getTranslation = (key, options?) => translate('assignmentTracker', key, options);

const getDateValue = (path: string) => (assignment: Assignment) => {
  const date = formatUiDate(new Date(get(assignment, path)));
  return <Typography variant="body1">{date}</Typography>;
};

export const StudentAssignmentList: React.FC<Props> = ({
  assignments,
  filters,
  onAddAssignmentClick
}) => {
  const theme = useTheme();
  const classes = useStyles(theme)();
  const navigate = useNavigate();
  const [, { openModal }] = useModal();

  const { studentId } = filters;
  const student = useSelector(state => getStudent(state, { studentId })) ?? null;
  const [attemptStatusFilter, setAttemptStatusFilter] = useState(null);

  const assignmentAttempts = useMemo(() => {
    return assignments.reduce((acc, assignment) => {
      const attempt = assignment.attempts?.find(attempt => studentId === attempt.studentId);
      if (attemptStatusFilter === 'all' && attempt) return [...acc, { assignment, attempt }];
      if (!attempt || (attemptStatusFilter && attemptStatusFilter !== attempt.status)) return acc;
      return [...acc, { assignment, attempt }];
    }, [] as AssignmentAttempt[]);
  }, [assignments, attemptStatusFilter, studentId]);

  const handleChangeAttemptStatusFilter = (_, value) => {
    setAttemptStatusFilter(value);
  };

  const handleFilesClick = e => {
    const assignmentAttempt = assignmentAttempts.find(
      assignmentAttempt => assignmentAttempt.assignment.id === Number(e.currentTarget.value)
    );

    if (!assignmentAttempt) return;

    openModal({
      Title: () => (
        <BaseModalTitle
          text={`${getTranslation('attemptFiles')}: ${assignmentAttempt.assignment.title}`}
        />
      ),
      body: <AttemptFilesModal files={assignmentAttempt.attempt.files} />
      // options: {
      //   classes: { titleVariant: 'h6' }
      // }
    });
  };

  const handlePreviousClick = () => navigate(-1);

  const getAssignmentStatus = ({ attempt }: AssignmentAttempt) => (
    <AttemptStatusChip status={attempt.status} />
  );

  const getAssignmentAttemptFiles = ({ assignment, attempt }: AssignmentAttempt) => {
    if (!attempt.files?.length) {
      return <Typography variant="body1">{getTranslation('noAttemptFiles')}</Typography>;
    }

    return (
      <Button
        color="primary"
        value={assignment.id}
        onClick={handleFilesClick}
        className={classes.fileText}
        disableRipple
      >
        <Typography variant="body1">
          {getTranslation('files', { fileCount: attempt.files.length })}
        </Typography>
      </Button>
    );
  };

  const columns: TableColumnProps<any>[] = [
    {
      label: getTranslation('title'),
      getValue({ assignment }): React.ReactElement {
        return (
          <RouterLink
            to={`assignment/${assignment.id}/students/${student?.id}`}
            className={classes.link}
          >
            <Typography variant="h6" className={classes.link}>
              {assignment.title}
            </Typography>
          </RouterLink>
        );
      },
      options: {
        width: '25%',
        sortable: true,
        sortKey: 'assignment.title'
      }
    },
    {
      label: getTranslation('dateAssigned'),
      getValue: getDateValue('assignment.dateAssigned'),
      options: {
        sortable: true,
        sortKey: 'assignment.dateAssigned'
      }
    },
    {
      label: getTranslation('dateDue'),
      getValue: getDateValue('assignment.dateDue'),
      options: {
        width: '15%',
        sortable: true,
        sortKey: 'assignment.dateDue'
      }
    },
    {
      label: getTranslation('attemptStatus'),
      getValue: getAssignmentStatus,
      options: {
        width: '21%',
        sortable: true,
        sortKey: 'attempt.status'
      }
    },
    {
      label: getTranslation('attemptFiles'),
      getValue: getAssignmentAttemptFiles,
      options: {
        width: '21%',
        sortable: false
      }
    },
    {
      label: '',
      getValue({ assignment }): React.ReactElement {
        return (
          <Box display="flex" alignItems="center" fontWeight="fontWeightBold">
            <RouterLink to={`assignment/${assignment.id}/students/${student?.id}`}>
              <IconButton key={`icon-button-${assignment.id}`}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </RouterLink>
          </Box>
        );
      },
      options: {
        width: '3%',
        sortable: false
      }
    }
  ];

  return (
    <Box py={3} px={5} bgcolor="primary.light" className={classes.container}>
      <Box display="flex">
        <SATableHeader
          title={`${student?.firstName} ${student?.lastName}`}
          renderBackArrow={false}
          handlePreviousClick={handlePreviousClick}
        />
        <Box ml="auto">
          <Typography>{getTranslation('attemptStatus')}</Typography>
          <AttemptStatusDropdown
            displayEmpty
            name="attemptStatusFilter"
            value={attemptStatusFilter ?? 'all'}
            onChange={handleChangeAttemptStatusFilter}
          />
        </Box>
      </Box>
      <SATableLayout
        data={assignmentAttempts}
        columns={columns}
        filters={[{ path: 'assignment.classroomId', compareTo: filters.classroomId }]}
        titleNoData={getTranslation('studentTitleNoData', {
          studentName: `${student?.firstName} ${student?.lastName}`
        })}
        subTitleNoData={getTranslation('subTitleNoData')}
        buttonTitleNoData={getTranslation('buttonTitleNoData')}
        handleNoDataClick={onAddAssignmentClick}
      />
    </Box>
  );
};

/*
    124px = HeightFooter + HeightHeader
*/
const footerAndHeaderHeight = 150;
const filtersHeight = 104;
const useStyles = theme =>
  makeStyles(() => ({
    container: {
      height: `calc(100vh - ${footerAndHeaderHeight + filtersHeight}px)`,
      overflow: 'auto'
    },
    messageChip: {
      backgroundColor: '#D12348'
    },
    labelChip: {
      color: '#FFF',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 400
    },
    link: () => ({
      color: `${theme.palette.text.primary} !important`,
      textDecoration: 'none !important',
      '&:hover': { color: 'inherit', textDecoration: 'underline !important' }
    }),
    fileText: {
      textTransform: 'none',
      padding: 0,
      width: 'inherit',
      height: 'inherit',
      minWidth: 'inherit',
      justifyContent: 'left'
    }
  }));

export default StudentAssignmentList;
